import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, MapPin, Truck, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { api } from '../lib/auth';
import { formatLocalISO } from '../lib/dateUtils';

/**
 * UserAssignmentHistory — Çalışanın planning atama geçmişini gösterir
 * (route, depot, müşteri, plaka, tarih). 90 gün geriye + 30 gün ileriye
 * pencere; default 200 kayıt cap. Hafta numarasına göre gruplanır.
 *
 * Kullanım yerleri:
 *   - AdminEmployeeProfile.jsx (admin başkasının profiline bakarken)
 *   - EmployeeProfile.jsx (kullanıcı kendi profilinde)
 *
 * Backend: GET /api/planning/users/{userId}/assignments
 */
export function UserAssignmentHistory({ userId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openWeeks, setOpenWeeks] = useState({});
  const [expanded, setExpanded] = useState(false);
  // Tarih aralığı filtresi — backend `days_back` parametresini kullanıyor;
  // 'all' seçildiğinde 730 gün geri (~2 yıl) = pratikte tüm tarihçe.
  const [rangeKey, setRangeKey] = useState('90');
  const RANGES = [
    { key: '30',  label: 'Son 30 gün',  days: 30 },
    { key: '90',  label: 'Son 90 gün',  days: 90 },
    { key: '180', label: 'Son 180 gün', days: 180 },
    { key: 'all', label: 'Tümü',        days: 730 },
  ];

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const range = RANGES.find((r) => r.key === rangeKey) || RANGES[1];
    api.get(`/planning/users/${userId}/assignments?days_back=${range.days}&days_ahead=30&limit=500`)
      .then((res) => setItems(res.data?.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    // RANGES sabit; dependency array'de yer almasına gerek yok.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, rangeKey]);

  // Filtre değişiminde varsayılan açık haftaları sıfırla — yeni veride
  // farklı haftalar olabilir.
  useEffect(() => {
    setOpenWeeks({});
    setExpanded(false);
  }, [rangeKey]);

  // Hafta bazında grupla. Backend `assignment_date` üzerinden newest-first
  // dönüyor; biz de Year-W## anahtarına göre topluyoruz.
  const groups = groupByWeek(items);
  const weekKeys = Object.keys(groups);
  // Default: en son 4 haftayı aç
  useEffect(() => {
    if (weekKeys.length > 0 && Object.keys(openWeeks).length === 0) {
      const initial = {};
      weekKeys.slice(0, 4).forEach((k) => { initial[k] = true; });
      setOpenWeeks(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  const visibleWeeks = expanded ? weekKeys : weekKeys.slice(0, 8);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Calendar className="h-4 w-4 text-emerald-500" /> Atama Geçmişi
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-400">Yükleniyor…</CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid={`user-assignment-history-${userId}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
          <Calendar className="h-4 w-4 text-emerald-500" />
          Atama Geçmişi
          {items.length > 0 && (
            <Badge variant="outline" className="ml-1 text-[10px]">{items.length}</Badge>
          )}
          {/* Tarih aralığı toggle — segmented control. Mobilde wrap eder. */}
          <div className="ml-auto inline-flex rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden text-[10px]" data-testid="user-assignment-range-toggle">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                onClick={() => setRangeKey(r.key)}
                className={`px-2 py-0.5 transition-colors ${
                  rangeKey === r.key
                    ? 'bg-emerald-500 text-white font-semibold'
                    : 'bg-white dark:bg-slate-800 text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/40'
                }`}
                data-testid={`user-assignment-range-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length === 0 ? (
          <div className="text-xs text-slate-400 italic">
            {rangeKey === 'all' ? 'Hiç atama yok' : `Son ${RANGES.find((r) => r.key === rangeKey)?.days || 90} günde atama yok`}
          </div>
        ) : (
          <>
            {visibleWeeks.map((wk) => {
              const rows = groups[wk];
              const open = !!openWeeks[wk];
              return (
                <div key={wk} className="border border-slate-200 dark:border-slate-700 rounded">
                  <button
                    type="button"
                    onClick={() => setOpenWeeks((s) => ({ ...s, [wk]: !s[wk] }))}
                    className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700/40"
                    data-testid={`user-assignment-week-${wk}`}
                  >
                    {open
                      ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                      : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                    <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{wk}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{rows.length} atama</span>
                    {/* Mini doluluk barı — 7 gün, dolu günler emerald, boş günler
                        gri. Operatör hangi haftada yoğun çalıştığını bir
                        bakışta görür (Pzt → Paz). */}
                    <WeekFillBar rows={rows} />
                    {rows[0]?.assignment_date && (
                      <span className="ml-1 text-[10px] text-slate-400">
                        {new Date(rows[rows.length - 1].assignment_date).toLocaleDateString('tr', { day: '2-digit', month: 'short' })}
                        {' → '}
                        {new Date(rows[0].assignment_date).toLocaleDateString('tr', { day: '2-digit', month: 'short' })}
                      </span>
                    )}
                  </button>
                  {open && (
                    <ul className="divide-y divide-slate-100 dark:divide-slate-700/50">
                      {rows.map((a) => (
                        <AssignmentRow key={a.id} a={a} />
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
            {weekKeys.length > 8 && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="w-full text-[11px] text-emerald-600 hover:underline pt-1"
              >
                {expanded ? 'Daha az göster' : `+${weekKeys.length - 8} hafta daha göster`}
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function AssignmentRow({ a }) {
  const plate = a.vehicle_plate || a.vehicle_code;
  // Planning sayfasına deep-link — operatör burada rota koduna tıklarsa
  // o haftanın board'una gider, ilgili müşteri otomatik filter olarak
  // uygulanmadan açılır (Planning.jsx zaten ?year&week parametrelerini
  // okuyor — ek yönlendirme değişikliği gerekmiyor).
  const planningHref = a.year && a.week_number
    ? `/dashboard/planning?year=${a.year}&week=${a.week_number}`
    : null;
  return (
    <li className="px-2.5 py-1.5 text-xs flex items-center gap-2 flex-wrap" data-testid={`user-assignment-row-${a.id}`}>
      <span className="font-mono text-[10px] text-slate-500 w-16 shrink-0">
        {new Date(a.assignment_date).toLocaleDateString('tr', { weekday: 'short', day: '2-digit', month: 'short' })}
      </span>
      {a.company_name && (
        <Badge
          variant="outline"
          className="text-[10px] font-semibold"
          style={a.company_color ? { borderColor: a.company_color, color: a.company_color } : {}}
        >
          {a.company_name}
        </Badge>
      )}
      {planningHref ? (
        <Link
          to={planningHref}
          className="font-semibold text-emerald-600 hover:text-emerald-700 hover:underline truncate max-w-[140px] inline-flex items-center gap-0.5"
          data-testid={`user-assignment-route-link-${a.id}`}
          title="Planning sayfasında bu haftayı aç"
        >
          {a.route_code || a.route_name || '—'}
          <ExternalLink className="h-2.5 w-2.5 opacity-60" />
        </Link>
      ) : (
        <span className="font-semibold text-slate-700 dark:text-slate-200 truncate max-w-[140px]">
          {a.route_code || a.route_name || '—'}
        </span>
      )}
      {a.depot_name && (
        <span className="text-[10px] text-slate-500 inline-flex items-center gap-0.5">
          <MapPin className="h-2.5 w-2.5" />{a.depot_name}
        </span>
      )}
      {plate && (
        <span className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700/50 text-blue-700 dark:text-blue-300">
          <Truck className="h-2.5 w-2.5" />{plate}
        </span>
      )}
      {a.status && a.status !== 'assigned' && (
        <Badge variant="secondary" className="text-[9px] uppercase">{a.status}</Badge>
      )}
    </li>
  );
}

/**
 * 7 günlük mini doluluk barı. ISO-week mantığı: Pzt..Paz. `rows` haftanın
 * tüm atamaları → her gün için dolu/boş cell. Aynı gün birden fazla atama
 * varsa (multi-route) yine 1 dolu sayılır. ml-auto ile sağa yaslanmaz —
 * tarih label'ı en sağda kalsın diye middle gibi davranır.
 */
function WeekFillBar({ rows }) {
  const days = new Set(rows.map((a) => a.assignment_date).filter(Boolean));
  // Haftanın Pazartesi tarihini bul: en küçük (oldest) tarihten geriye doğru
  // gün sayarak. Backend ISO week_number üzerinden tutarlı veri döndürdüğü
  // için ilk satırın tarihinden Pzt'yi çıkarmak güvenli.
  const sample = rows[0]?.assignment_date;
  if (!sample) return null;
  const d = new Date(sample);
  // ISO Pzt=1, Paz=7 → JS getDay() Paz=0..Cmt=6 → ISO=((getDay()+6)%7)+1
  const isoDow = ((d.getDay() + 6) % 7) + 1;
  const monday = new Date(d);
  monday.setDate(d.getDate() - (isoDow - 1));
  const cells = [];
  for (let i = 0; i < 7; i += 1) {
    const dt = new Date(monday);
    dt.setDate(monday.getDate() + i);
    // Yerel tarih (UTC değil) — bkz lib/dateUtils.formatLocalISO
    const iso = formatLocalISO(dt);
    const filled = days.has(iso);
    cells.push(
      <div
        key={i}
        className={`h-3 w-3 rounded-sm border ${
          filled
            ? 'bg-emerald-500 border-emerald-600 dark:bg-emerald-400 dark:border-emerald-500'
            : 'bg-slate-100 border-slate-200 dark:bg-slate-700/40 dark:border-slate-600'
        }`}
        title={`${dt.toLocaleDateString('tr', { weekday: 'short', day: '2-digit', month: 'short' })} — ${filled ? 'dolu' : 'boş'}`}
      />,
    );
  }
  return (
    <div className="flex items-center gap-0.5 ml-auto" data-testid="user-assignment-week-fillbar">
      {cells}
    </div>
  );
}

// year-W## anahtarı altında gruplar; yıl-hafta-no birleşimi unique.
function groupByWeek(items) {
  const out = {};
  for (const a of items) {
    const yr = a.year ?? '?';
    const wk = String(a.week_number ?? '?').padStart(2, '0');
    const key = `${yr}-W${wk}`;
    if (!out[key]) out[key] = [];
    out[key].push(a);
  }
  // Her grup içinde tarih DESC sırada (backend zaten DESC dönüyor). Anahtar
  // sırasını koruyacak şekilde insertion order güveniyoruz: backend
  // newest-first → ilk gelen hafta en yeni hafta.
  return out;
}
